import { Progress } from "@/components/ui/progress";
import { Link } from "react-router-dom";
import { useReviews } from "@/hooks/use-review";
import { Star, Loader2 } from "lucide-react";

const RatingComponent = ({ astrologerId, compact = false }) => {
  const { loading, reviews, rating } = useReviews();

  const calculateReviewStats = () => {
    if (!reviews || reviews.length === 0) {
      return {
        rating: 0,
        reviewCount: 0,
        distribution: [0, 0, 0, 0, 0],
        countMap: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
      };
    }

    const totalRating = reviews.reduce((sum, review) => sum + review.rating, 0);
    const averageRating = totalRating / reviews.length;

    const distribution = {};
    for (let i = 1; i <= 5; i++) {
      distribution[i] = reviews.filter(review => review.rating === i).length;
    }

    const distributionArray = [];
    for (let i = 5; i >= 1; i--) {
      const percentage = (distribution[i] / reviews.length) * 100;
      distributionArray.push(Math.round(percentage));
    }

    return {
      rating: averageRating,
      reviewCount: reviews.length,
      distribution: distributionArray,
      countMap: distribution
    };
  };

  const stats = calculateReviewStats();

  const renderStars = (size = "text-base") => {
    const stars = [];
    const fullStars = Math.floor(stats.rating);
    const hasHalfStar = stats.rating % 1 >= 0.5;

    for (let i = 0; i < fullStars; i++) {
      stars.push(<Star key={`star-${i}`} className={`${size} text-yellow-400 fill-yellow-400`} />);
    }

    if (hasHalfStar && fullStars < 5) {
      stars.push(<Star key="half-star" className={`${size} text-yellow-400 fill-yellow-400 opacity-50`} />);
    }

    const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
    for (let i = 0; i < emptyStars; i++) {
      stars.push(<Star key={`empty-${i}`} className={`${size} text-gray-300`} />);
    }

    return stars;
  };

  if (loading) {
    return (
      <div className="p-3 flex justify-center items-center min-h-[120px]">
        <Loader2 className="h-5 w-5 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-3">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-base font-semibold text-gray-900">Reviews</h2>
        <Link to="/reviews" className="text-xs text-orange-500 hover:text-orange-600">
          View All
        </Link>
      </div>

      <div className="flex items-start gap-4">
        {/* Rating Score */}
        <div className="text-center">
          <div className="text-3xl font-bold text-gray-900">
            {stats.rating ? stats.rating.toFixed(1) : "0.0"}
          </div>
          <div className="flex gap-0.5 justify-center my-1">
            {renderStars("h-3 w-3")}
          </div>
          <div className="text-xs text-gray-500">{stats.reviewCount} reviews</div>
        </div>

        {/* Distribution Bars */}
        <div className="flex-1 space-y-1">
          {[5, 4, 3, 2, 1].map((starCount, index) => (
            <div key={`dist-${starCount}`} className="flex items-center gap-1">
              <span className="text-xs text-gray-600 w-3">{starCount}</span>
              <Star className="h-3 w-3 text-yellow-400 fill-yellow-400" />
              <div className="flex-1">
                <Progress
                  value={stats.distribution[index]}
                  className="h-1.5 bg-gray-200"
                />
              </div>
              <span className="text-xs text-gray-500 w-4 text-right">
                {stats.countMap[starCount]}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RatingComponent;
